import type { IMenuProps, IOmit } from '@sixui/ui';
import { faPen, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Menu, MenuItem } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetLiveStreamsItemResponseBody } from '~/lib/api/modules/LiveStreams';
import { useNavigate } from '~/components/utils/Navigation';
import { useDeleteLiveStreamMutation } from '~/lib/api/modules/LiveStreams/hooks/useDeleteLiveStreamMutation';

export type ILiveStreamListItemMenuProps = IContainerProps &
  IOmit<IMenuProps, 'trigger' | 'children'> & {
    liveStream: IApiGetLiveStreamsItemResponseBody;
  };

export const LiveStreamListItemMenu: React.FC<ILiveStreamListItemMenuProps> = (
  props,
) => {
  const { sx, liveStream, ...other } = props;
  const { t } = useTranslation();
  const deleteLiveStreamMutation = useDeleteLiveStreamMutation();
  const navigate = useNavigate();

  const processing = deleteLiveStreamMutation.isLoading;

  const handleDelete = (): void => {
    if (deleteLiveStreamMutation.isLoading) {
      return;
    }

    // eslint-disable-next-line no-alert
    const doDeletion = confirm(
      t('LiveStreamListItemMenu.dialog.confirmDeletion.text', {
        name: liveStream.displayName,
      }),
    );

    if (doDeletion) {
      void deleteLiveStreamMutation
        .mutateAsync({
          appId: liveStream.appId,
          liveStreamId: liveStream._id,
        })
        .then(() => {
          navigate(`/apps/${liveStream.appId}/liveStreams`);
        })
        .catch(() => {
          // TODO: handle error (snackbar)
        });
    }
  };

  return (
    <Menu
      trigger={({ getProps }) => (
        <IconButton
          icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
          loading={processing}
          {...getProps()}
        />
      )}
      placement="bottom-end"
      sx={sx}
      {...other}
    >
      <MenuItem
        label={t('common.edit')}
        leadingIcon={<FontAwesomeIcon icon={faPen} />}
        href={`/apps/${liveStream.appId}/liveStreams/${liveStream._id}/update`}
      />
      <MenuItem
        label={t('common.delete')}
        leadingIcon={<FontAwesomeIcon icon={faTrashCan} />}
        onClick={handleDelete}
        variant="danger"
      />
    </Menu>
  );
};
